import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  bulkResolveMatchingV2,
  rebindSpeakerV2,
  resolveMatchingV2,
  unbindMatchingV2,
} from "@/api/matching";
import type {
  EmployeeSummary,
  MatchingV2Response,
  MatchingV2Speaker,
  MeetingCard,
  TranscriptUtterance,
  UUID,
} from "@/api/types";
import { InlineSpeakerBadge } from "@/components/matching/InlineSpeakerBadge";
import { InconsistenciesBlock } from "@/components/meeting-card/InconsistenciesBlock";
import { RerunWalkthrough } from "@/components/meeting-card/RerunWalkthrough";
import { SpeakerSummaryBar } from "@/components/meeting-card/SpeakerSummaryBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface TranscriptTabProps {
  card: MeetingCard;
  /**
   * v1.1.4 — when set, scrolls to the utterance whose `start_ms` is the
   * largest value ≤ `highlightStartMs` and pulses a yellow background for
   * ~2s. `jumpTick` lets the parent re-trigger the same target (e.g. the
   * operator clicks the same citation twice in a row).
   */
  highlightStartMs?: number | null;
  jumpTick?: number;
  /**
   * matching-v2 §7.1. When the backend ships the v2 shape (presence of
   * `transcript_revision`), the parent passes the snapshot down so each
   * utterance row can render the inline speaker badge. Null = no v2
   * payload available, fall back to plain `meetings.card.speaker` labels.
   */
  matchingV2?: MatchingV2Response | null;
  /**
   * Employees in the meeting's participants list. Drives the «Не нашли»
   * combobox first group.
   */
  participants?: EmployeeSummary[];
  /**
   * Full employee registry. Drives the «Не нашли» combobox second group
   * and the «Перепривязать всего спикера» picker.
   */
  registry?: EmployeeSummary[];
  /**
   * matching-v2 §7.2 — POSTs to `/api/meetings/:id/participants/add`.
   * Drives the «+ Добавить» button inside `InconsistenciesBlock`. Left
   * undefined → the button stays disabled (no inflight mutation).
   */
  onAddParticipant?: (employeeId: UUID) => Promise<void>;
}

/**
 * FR-14 — Транскрипт с поиском и кликабельными таймкодами.
 *
 * AssemblyAI отдаёт `start_ms` / `end_ms` на каждую utterance; внутри UI
 * мы показываем их в `[mm:ss]` (или `[hh:mm:ss]` для длинных записей).
 * Клик по таймкоду копирует значение в буфер — плеер вне scope v1, но
 * операторам удобно вставить таймкод в комментарий/тикет.
 */
export function TranscriptTab({
  card,
  highlightStartMs,
  jumpTick,
  matchingV2 = null,
  participants = [],
  registry = [],
  onAddParticipant,
}: TranscriptTabProps) {
  const { t } = useTranslation();
  // Speaker-row click + walk-through step → forward to the existing
  // `highlightStartMs` / `jumpTick` pipeline. Look up the first
  // utterance whose `speaker` matches the label, then bump the jump
  // tick so Dialog re-runs its scroll-and-flash effect.
  const [internalHighlight, setInternalHighlight] = useState<number | null>(
    null,
  );
  const [internalJump, setInternalJump] = useState(0);
  const firstStartByLabel = useMemo(() => {
    const m = new Map<string, number>();
    const list = card.transcript?.utterances ?? [];
    for (const u of list) {
      if (!m.has(u.speaker)) m.set(u.speaker, u.start_ms);
    }
    return m;
  }, [card.transcript]);
  const focusSpeakerLabel = (label: string) => {
    const start = firstStartByLabel.get(label);
    if (start === undefined) return;
    setInternalHighlight(start);
    setInternalJump((n) => n + 1);
  };
  if (card.transcript === null) {
    return (
      <p className="text-sm text-muted-foreground">
        {t("meetings.card.transcriptEmpty")}
      </p>
    );
  }
  const { utterances, text } = card.transcript;
  // Internal focus wins over the parent's prop when an operator
  // clicked a summary row; the parent's prop is the fallback for the
  // FR-14 deep-link case (citation → utterance) it was originally
  // designed for.
  const effectiveHighlight =
    internalHighlight !== null ? internalHighlight : (highlightStartMs ?? null);
  const effectiveJumpTick =
    internalHighlight !== null ? internalJump : (jumpTick ?? 0);
  return (
    <div className="space-y-3" data-testid="transcript-tab">
      {matchingV2 !== null ? (
        <>
          <RerunWalkthrough
            preservedBindingsCount={matchingV2.preserved_bindings_count}
            speakers={matchingV2.speakers}
            onFocusSpeaker={focusSpeakerLabel}
          />
          <SpeakerSummaryBar
            speakers={matchingV2.speakers}
            onSpeakerClick={focusSpeakerLabel}
          />
          <InconsistenciesBlock
            unmatchedMentions={matchingV2.unmatched_mentions}
            inconsistencies={matchingV2.inconsistencies}
            onAddParticipant={onAddParticipant}
          />
          <MatchingV2Toolbar meetingId={card.id} matching={matchingV2} />
        </>
      ) : null}
      {utterances.length > 0 ? (
        <Dialog
          utterances={utterances}
          highlightStartMs={effectiveHighlight}
          jumpTick={effectiveJumpTick}
          meetingId={card.id}
          matchingV2={matchingV2}
          participants={participants}
          registry={registry}
        />
      ) : (
        <pre className="whitespace-pre-wrap rounded bg-muted p-3 text-sm">
          {text}
        </pre>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// matching-v2 helpers
// ─────────────────────────────────────────────────────────────────────────

/**
 * Index the v2 snapshot by `speaker.label` so each utterance row can do
 * an O(1) lookup against the diarization label coming from AssemblyAI.
 */
function indexSpeakers(
  matching: MatchingV2Response | null,
): Map<string, MatchingV2Speaker> {
  const out = new Map<string, MatchingV2Speaker>();
  if (matching === null) return out;
  for (const sp of matching.speakers) {
    out.set(sp.label, sp);
  }
  return out;
}

// Hook into the central react-query cache key the parent uses
// (`["meeting-matching", meetingId]`). Every mutation in this file
// invalidates that key so the snapshot refetches in one shot.
const MATCHING_V2_KEY = (meetingId: UUID): readonly unknown[] => [
  "meeting-matching",
  meetingId,
];

interface MatchingV2ToolbarProps {
  meetingId: UUID;
  matching: MatchingV2Response;
}

function MatchingV2Toolbar({ meetingId, matching }: MatchingV2ToolbarProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [undoState, setUndoState] = useState<{
    labels: string[];
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);

  const bulkResolve = useMutation({
    mutationFn: () =>
      bulkResolveMatchingV2(meetingId, {
        expected_revision: matching.transcript_revision,
      }),
    onSuccess: (resp) => {
      queryClient.setQueryData(MATCHING_V2_KEY(meetingId), resp.snapshot);
      // Schedule the toast auto-dismiss after the 10-sec undo window
      // matches §3.3 `pending_until`. The window is also enforced
      // server-side; this is purely a UI affordance.
      const timer = setTimeout(() => setUndoState(null), 10_000);
      setUndoState({ labels: resp.applied_labels, timer });
    },
  });

  const undo = useMutation({
    mutationFn: async (labels: string[]) => {
      for (const label of labels) {
        await unbindMatchingV2(meetingId, {
          speaker_label: label,
          expected_revision: matching.transcript_revision,
        });
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: MATCHING_V2_KEY(meetingId),
      });
      setUndoState(null);
    },
  });

  // Count of `medium_unique` speakers — drives the «Применить все
  // уверенные» button visibility per §7.2. We treat as «уверенный» both
  // the explicit band and any speaker whose top candidate would pass the
  // bulk-resolve filter; band='high' is auto-resolved before the
  // operator sees the UI, so the count is medium_unique only.
  const mediumUniqueCount = useMemo(
    () =>
      matching.speakers.filter(
        (sp) =>
          sp.binding === null &&
          sp.candidates[0]?.confidence_band === "medium_unique",
      ).length,
    [matching.speakers],
  );

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-2 border-b border-input pb-2"
      data-testid="matching-v2-toolbar"
    >
      <div className="flex items-center gap-2">
        {mediumUniqueCount > 0 ? (
          <Button
            size="sm"
            onClick={() => bulkResolve.mutate()}
            disabled={bulkResolve.isPending}
            data-testid="matching-v2-bulk-resolve"
          >
            {bulkResolve.isPending
              ? t("meetings.card.matchingV2.toolbar.bulkResolveBusy")
              : t("meetings.card.matchingV2.toolbar.bulkResolve", {
                  count: mediumUniqueCount,
                })}
          </Button>
        ) : null}
      </div>
      {undoState !== null ? (
        <div
          className="flex items-center gap-2 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-900"
          role="status"
          data-testid="matching-v2-bulk-undo"
        >
          <span>
            {t("meetings.card.matchingV2.toolbar.bulkApplied", {
              count: undoState.labels.length,
            })}
          </span>
          <button
            type="button"
            onClick={() => {
              clearTimeout(undoState.timer);
              undo.mutate(undoState.labels);
            }}
            className="font-medium underline hover:no-underline"
            data-testid="matching-v2-bulk-undo-btn"
          >
            {t("meetings.card.matchingV2.toolbar.undo")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Pick the utterance whose `start_ms` is the largest value still ≤ the
 * target, or the first utterance when the target precedes all of them.
 * Exported for unit-test access.
 */
export function pickClosestUtteranceIndex(
  utterances: TranscriptUtterance[],
  targetMs: number,
): number {
  if (utterances.length === 0) return -1;
  let bestIndex = 0;
  for (let i = 0; i < utterances.length; i++) {
    if (utterances[i]!.start_ms <= targetMs) {
      bestIndex = i;
    } else {
      break;
    }
  }
  return bestIndex;
}

/**
 * Format milliseconds → `mm:ss` (or `hh:mm:ss` past the hour mark).
 * Exported for the unit-test suite and any future timeline integration.
 */
export function formatTimestamp(ms: number): string {
  const safe = Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 1000) : 0;
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const mm = String(minutes).padStart(2, "0");
  const ss = String(seconds).padStart(2, "0");
  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
}

function useDebouncedValue<T>(value: T, delay = 200): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handle);
  }, [value, delay]);
  return debounced;
}

/**
 * Best-effort clipboard write. Mirrors the pattern from
 * `InviteFormDialog` (secure-context `navigator.clipboard` first, then
 * legacy `execCommand("copy")` fallback for plain-http deployments).
 */
async function copyToClipboard(value: string): Promise<boolean> {
  try {
    if (
      typeof window !== "undefined" &&
      window.isSecureContext &&
      navigator?.clipboard?.writeText !== undefined
    ) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // fall through to execCommand fallback
  }
  try {
    if (typeof document === "undefined") return false;
    const ta = document.createElement("textarea");
    ta.value = value;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

interface DialogProps {
  utterances: TranscriptUtterance[];
  highlightStartMs: number | null;
  jumpTick: number;
  meetingId: UUID;
  matchingV2: MatchingV2Response | null;
  participants: EmployeeSummary[];
  registry: EmployeeSummary[];
}

function Dialog({
  utterances,
  highlightStartMs,
  jumpTick,
  meetingId,
  matchingV2,
  participants,
  registry,
}: DialogProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 200);
  const normalisedQuery = debouncedQuery.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (normalisedQuery.length === 0) return utterances;
    return utterances.filter((u) =>
      u.text.toLowerCase().includes(normalisedQuery),
    );
  }, [utterances, normalisedQuery]);

  // Resolve the highlight target against the *unfiltered* utterance list so
  // jump-to-transcript still works while the operator has a search query
  // applied. We also clear the search if the target is filtered out, so the
  // utterance is actually visible.
  const targetStartMs = useMemo(() => {
    if (highlightStartMs === null) return null;
    const idx = pickClosestUtteranceIndex(utterances, highlightStartMs);
    if (idx < 0) return null;
    return utterances[idx]!.start_ms;
  }, [utterances, highlightStartMs]);

  // If the target utterance is currently filtered out by the search query,
  // clear the search so the highlight scroll has something to land on.
  useEffect(() => {
    if (targetStartMs === null) return;
    if (normalisedQuery.length === 0) return;
    const visible = filtered.some((u) => u.start_ms === targetStartMs);
    if (!visible) setQuery("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetStartMs, jumpTick]);

  const speakerIndex = useMemo(() => indexSpeakers(matchingV2), [matchingV2]);

  return (
    <div className="space-y-3">
      <Input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("meetings.card.transcript.searchPlaceholder")}
        aria-label={t("meetings.card.transcript.searchAria")}
        data-testid="transcript-search"
      />
      {filtered.length === 0 ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="transcript-empty-search"
        >
          {t("meetings.card.transcript.noMatches")}
        </p>
      ) : (
        <ol className="space-y-3" data-testid="transcript-dialog">
          {filtered.map((u, i) => (
            <UtteranceRow
              key={`${u.start_ms}-${i}`}
              utterance={u}
              query={normalisedQuery}
              isHighlightTarget={
                targetStartMs !== null && u.start_ms === targetStartMs
              }
              jumpTick={jumpTick}
              meetingId={meetingId}
              matchingRevision={matchingV2?.transcript_revision ?? null}
              speakerMatch={speakerIndex.get(u.speaker) ?? null}
              participants={participants}
              registry={registry}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

interface UtteranceRowProps {
  utterance: TranscriptUtterance;
  /** Already lower-cased + trimmed; empty string means "no filter". */
  query: string;
  isHighlightTarget?: boolean;
  jumpTick?: number;
  meetingId?: UUID;
  matchingRevision?: number | null;
  /** matching-v2 speaker snapshot for `utterance.speaker`, when available. */
  speakerMatch?: MatchingV2Speaker | null;
  participants?: EmployeeSummary[];
  registry?: EmployeeSummary[];
}

function UtteranceRow({
  utterance,
  query,
  isHighlightTarget = false,
  jumpTick = 0,
  meetingId,
  matchingRevision = null,
  speakerMatch = null,
  participants = [],
  registry = [],
}: UtteranceRowProps) {
  const { t } = useTranslation();
  const timestamp = formatTimestamp(utterance.start_ms);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "error">(
    "idle",
  );
  const [isFlashing, setIsFlashing] = useState(false);
  const liRef = useRef<HTMLLIElement | null>(null);

  useEffect(() => {
    if (copyState === "idle") return;
    const handle = setTimeout(() => setCopyState("idle"), 1500);
    return () => clearTimeout(handle);
  }, [copyState]);

  // Scroll + 2-second yellow flash when this row becomes the active jump
  // target. `jumpTick` is folded into the effect deps so repeated jumps
  // to the same utterance still re-trigger.
  useEffect(() => {
    if (!isHighlightTarget) return;
    setIsFlashing(true);
    if (
      liRef.current !== null &&
      typeof liRef.current.scrollIntoView === "function"
    ) {
      liRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const handle = setTimeout(() => setIsFlashing(false), 2000);
    return () => clearTimeout(handle);
  }, [isHighlightTarget, jumpTick]);

  const handleCopy = async () => {
    const ok = await copyToClipboard(timestamp);
    setCopyState(ok ? "copied" : "error");
  };

  return (
    <li
      ref={liRef}
      className={cn(
        "flex gap-3 rounded text-sm transition-colors duration-700",
        isFlashing ? "bg-yellow-100 dark:bg-yellow-900/40" : "bg-transparent",
      )}
      data-testid="transcript-utterance"
      data-highlight={isHighlightTarget ? "true" : undefined}
      data-flashing={isFlashing ? "true" : undefined}
    >
      <span className="flex w-32 shrink-0 flex-col gap-1">
        <button
          type="button"
          onClick={handleCopy}
          className={cn(
            "self-start rounded font-mono text-xs tabular-nums text-muted-foreground",
            "hover:text-foreground hover:underline focus-visible:outline-none",
            "focus-visible:ring-1 focus-visible:ring-ring",
          )}
          aria-label={t("meetings.card.transcript.timestampAria", {
            time: timestamp,
          })}
          data-testid="transcript-timestamp"
          data-start-ms={utterance.start_ms}
        >
          [{timestamp}]
        </button>
        {speakerMatch !== null &&
        meetingId !== undefined &&
        matchingRevision !== null ? (
          <UtteranceSpeakerBadge
            meetingId={meetingId}
            revision={matchingRevision}
            speaker={speakerMatch}
            participants={participants}
            registry={registry}
          />
        ) : (
          <span className="font-medium text-muted-foreground">
            {t("meetings.card.speaker", { label: utterance.speaker })}
          </span>
        )}
        {copyState !== "idle" && (
          <span
            className={cn(
              "text-xs",
              copyState === "copied"
                ? "text-muted-foreground"
                : "text-destructive",
            )}
            role="status"
            data-testid="transcript-copy-status"
          >
            {copyState === "copied"
              ? t("meetings.card.transcript.timestampCopied")
              : t("meetings.card.transcript.copyFailed")}
          </span>
        )}
      </span>
      <span className="whitespace-pre-wrap">
        <HighlightedText text={utterance.text} query={query} />
      </span>
    </li>
  );
}

interface HighlightedTextProps {
  text: string;
  /** Already lower-cased + trimmed; empty string disables highlight. */
  query: string;
}

/**
 * Split `text` on case-insensitive `query` occurrences and wrap matches in
 * `<mark>`. Renders raw text when the query is empty — no allocation cost
 * per utterance on first paint.
 */
export function HighlightedText({ text, query }: HighlightedTextProps) {
  if (query.length === 0) return <>{text}</>;
  const lower = text.toLowerCase();
  const parts: Array<{ value: string; match: boolean }> = [];
  let cursor = 0;
  while (cursor < text.length) {
    const idx = lower.indexOf(query, cursor);
    if (idx === -1) {
      parts.push({ value: text.slice(cursor), match: false });
      break;
    }
    if (idx > cursor) {
      parts.push({ value: text.slice(cursor, idx), match: false });
    }
    parts.push({
      value: text.slice(idx, idx + query.length),
      match: true,
    });
    cursor = idx + query.length;
  }
  return (
    <>
      {parts.map((p, i) =>
        p.match ? (
          <mark
            key={i}
            className="rounded bg-yellow-200 px-0.5 text-foreground dark:bg-yellow-700"
            data-testid="transcript-mark"
          >
            {p.value}
          </mark>
        ) : (
          <span key={i}>{p.value}</span>
        ),
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// matching-v2 — inline-badge wrapper that owns the resolve/rebind mutations
// ─────────────────────────────────────────────────────────────────────────

interface UtteranceSpeakerBadgeProps {
  meetingId: UUID;
  revision: number;
  speaker: MatchingV2Speaker;
  participants: EmployeeSummary[];
  registry: EmployeeSummary[];
}

/**
 * Owns the two speaker-scoped mutations exposed by the §3 API:
 *
 *   • `onResolve(employee_id)` → `POST /matching/resolve`
 *     The «single utterance» resolution: the operator picks an employee
 *     for the current speaker label. In v2 (per §3.4 the «scope» reserved)
 *     this is the same wire call as the operator's chosen «Только эта»
 *     branch of the rebind confirmation dialog.
 *
 *   • `onRebindAll(employee_id)` → `POST /matching/rebind-speaker`
 *     Cascade across every utterance of the speaker. The confirm dialog
 *     is owned by `InlineSpeakerBadge`; this wrapper only fires the
 *     mutation once the operator clicks «[Да]».
 *
 * Every mutation overwrites the central `["meeting-matching", meetingId]`
 * cache with the response shape — wholesale swap, no patches (matches
 * §4 «every mutation returns the same snapshot shape»).
 */
function UtteranceSpeakerBadge({
  meetingId,
  revision,
  speaker,
  participants,
  registry,
}: UtteranceSpeakerBadgeProps) {
  const queryClient = useQueryClient();
  const writeSnapshot = (snapshot: MatchingV2Response) => {
    queryClient.setQueryData(MATCHING_V2_KEY(meetingId), snapshot);
  };

  const resolve = useMutation({
    mutationFn: (employeeId: UUID) =>
      resolveMatchingV2(meetingId, {
        speaker_label: speaker.label,
        employee_id: employeeId,
        expected_revision: revision,
      }),
    onSuccess: (resp) => writeSnapshot(resp),
  });

  const rebindAll = useMutation({
    mutationFn: (employeeId: UUID) =>
      rebindSpeakerV2(meetingId, {
        speaker_label: speaker.label,
        employee_id: employeeId,
        scope: "all",
        expected_revision: revision,
      }),
    onSuccess: (resp) => writeSnapshot(resp),
  });

  return (
    <InlineSpeakerBadge
      speaker={speaker}
      participants={participants}
      registry={registry}
      onResolve={(employeeId) => resolve.mutate(employeeId)}
      onRebindAll={(employeeId) => rebindAll.mutate(employeeId)}
    />
  );
}
